import React, { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { supabase } from './supabase';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import {
  Plus,
  DollarSign,
  AlertTriangle,
  Calendar,
  Award,
  LogOut,
  Search,
  UserPlus,
  Check,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  ArrowLeft,
  Menu,
  X as CloseIcon,
  LayoutDashboard,
  Receipt,
  Users,
  Wallet,
  BarChart3,
  Target,
  Eye,
  Settings as SettingsIcon,
  Building2
} from 'lucide-react';
import { AuthProvider, useAuth } from './AuthContext';
import { ThemeProvider } from './ThemeContext';
import ProtectedRoute from './ProtectedRoute';
import ResetPasswordPage from './ResetPasswordPage';
import ZoomControl from './ZoomControl';
import UpdateBanner from './UpdateBanner';
import { useLoans, usePayments } from './useSupabase';
import { useProfile } from './useProfile';
import { useOrgRoster } from './useOrgRoster';
import { useAnimatedNumber } from './useAnimatedNumber';
import DatePicker from './DatePicker';

// Lazy-loaded: none of these are needed for the default Tablero view, and
// several (MetricsDashboard, GoalsPerformanceTracker, AuditLog, Settings)
// are admin-only -- a gestor's session never needs to download their code
// or the recharts library they pull in at all.
const Customers = lazy(() => import('./Customers'));
const CashControlSystem = lazy(() => import('./CashControlSystem'));
const MetricsDashboard = lazy(() => import('./MetricsDashboard'));
const GoalsPerformanceTracker = lazy(() => import('./GoalsPerformanceTracker'));
const AuditLog = lazy(() => import('./AuditLog'));
const Settings = lazy(() => import('./Settings'));

const StatCard = ({ title, value, subtitle }) => {
  const animated = useAnimatedNumber(typeof value === 'number' ? value : 0);
  const displayValue = typeof value === 'number'
    ? `$${Math.round(animated).toLocaleString()}`
    : value;

  return (
    <div className="stat-card-neon p-6 rounded-lg transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-400">{title}</p>
          <p className="text-2xl font-bold text-teal-300">{displayValue}</p>
          {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
        </div>
        <DollarSign size={32} className="text-teal-300" />
      </div>
    </div>
  );
};

// Always show dates as MM/DD/YYYY, regardless of device locale. Appending
// a local midnight time to date-only strings avoids new Date("YYYY-MM-DD")
// parsing as UTC, which can roll back a day in negative-UTC timezones.
const formatDate = (value) => {
  const date = typeof value === 'string' ? new Date(`${value}T00:00:00`) : value;
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

// Fixed categorical order for per-branch pills, assigned by sorted branch
// name -- same convention as Métricas' branch-growth chart, so a branch
// always gets the same color and colors never get re-cycled just because
// the roster changes order.
const BRANCH_LABEL_COLORS = [
  { bg: 'bg-teal-500/20', text: 'text-teal-300' },
  { bg: 'bg-cyan-500/20', text: 'text-cyan-300' },
  { bg: 'bg-amber-500/20', text: 'text-amber-300' },
  { bg: 'bg-pink-500/20', text: 'text-pink-300' },
  { bg: 'bg-indigo-500/20', text: 'text-indigo-300' },
];

// Pure function of the raw loan row (plus wall-clock time) -- doesn't
// reference any component state, so it lives at module scope instead of
// being recreated on every render.
const calculateLoanStatus = (loan) => {
  const startDate = loan.start_date ? new Date(loan.start_date) : new Date(loan.startDate);
  const today = new Date();
  const daysElapsed = Math.floor((today - startDate) / (1000 * 60 * 60 * 24));

  const loanAmount = loan.amount || loan.loanAmount || 0;
  const duration = loan.term_months ? loan.term_months * 30 : (loan.duration || 30);
  const totalExpected = parseFloat((loan.total_expected || loan.totalExpected || (loanAmount * (1 + (loan.interest_rate || loan.interestRate || 0.1)))).toFixed(2));
  const totalPaid = loan.total_paid || loan.totalPaid || 0;
  const dailyPayment = loan.daily_payment || loan.dailyPayment || (totalExpected / duration);

  const expectedPaymentsByNow = Math.min(daysElapsed, duration) * dailyPayment;
  const overdueAmount = Math.max(0, expectedPaymentsByNow - totalPaid);
  const todaysDue = overdueAmount + (daysElapsed < duration ? dailyPayment : 0);
  const daysBehind = overdueAmount > 0 ? Math.floor(overdueAmount / dailyPayment) : 0;

  let status = 'Activo';
  if (totalPaid >= totalExpected) {
    status = 'Pagado';
  } else if (daysBehind > 0) {
    if (daysBehind >= 7) {
      status = 'Crítico';
    } else if (daysBehind >= 3) {
      status = 'Atrasado';
    } else {
      status = 'Pendiente';
    }
  }

  return {
    ...loan,
    id: loan.id,
    clientName: loan.client_name || loan.clientName || (loan.borrowers?.name) || 'Cliente',
    branch: loan.branch || 'Sin sucursal',
    manager: loan.manager || 'Sin gestor',
    loanAmount,
    totalExpected,
    totalPaid,
    dailyPayment,
    duration,
    startDate,
    daysElapsed: Math.max(0, daysElapsed),
    expectedToDate: expectedPaymentsByNow,
    overdueAmount,
    todaysDue,
    daysBehind,
    status,
    daysRemaining: Math.max(0, duration - daysElapsed),
    dueDate: loan.due_date || loan.dueDate || new Date(startDate.getTime() + duration * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  };
};

const LoanTrackerApp = () => {
  const { user, logout } = useAuth();
  
  const { loans, loading: loansLoading, addLoan, updateLoan } = useLoans(user?.email);
  const { payments, loading: paymentsLoading, addPayment } = usePayments(user?.email);
  const { profile, loading: profileLoading, isAdmin, isGestor, isSuperAdmin } = useProfile(user?.id);
  const { branches: rosterBranches, rawBranches: rosterRawBranches, gestores: rosterGestores, supervisors: rosterSupervisors, staff: rosterStaff, loading: rosterLoading, refresh: refreshRoster } = useOrgRoster(isAdmin);

  const [activeView, setActiveView] = useState('dashboard');
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [selectedManager, setSelectedManager] = useState('all');
  const [showNewLoanForm, setShowNewLoanForm] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [dashboardBranchView, setDashboardBranchView] = useState(null);
  const [dashboardSearchTerm, setDashboardSearchTerm] = useState('');
  const [loansBranchView, setLoansBranchView] = useState(null);
  const [loanSearchTerm, setLoanSearchTerm] = useState('');
  const [paymentSearchTerm, setPaymentSearchTerm] = useState('');
  const [collectionsBranchView, setCollectionsBranchView] = useState(null);
  const [collectionSearchTerm, setCollectionSearchTerm] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [detailLoan, setDetailLoan] = useState(null);
  const [detailPayment, setDetailPayment] = useState(null);
  const [branchQuery, setBranchQuery] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('sidebarCollapsed') === 'true');

  const toggleSidebar = () => {
    setSidebarCollapsed((collapsed) => {
      localStorage.setItem('sidebarCollapsed', String(!collapsed));
      return !collapsed;
    });
  };

  const navGroups = [
    {
      label: 'Operaciones',
      items: [
        { key: 'dashboard', label: 'Tablero', icon: LayoutDashboard },
        { key: 'loans', label: 'Préstamos', icon: DollarSign },
        { key: 'collections', label: 'Cobros del Día', icon: Calendar },
        { key: 'payments', label: 'Historial de Pagos', icon: Receipt },
        { key: 'customers', label: 'Clientes', icon: Users },
        { key: 'cashcontrol', label: 'Control de Efectivo', icon: Wallet },
      ],
    },
    {
      label: 'Análisis',
      items: [
        // Metrics stays admin-tier only; Metas & Bonos is visible to a
        // gestor too, scoped to just her own card (see the goals view).
        ...(isAdmin ? [{ key: 'metrics', label: 'Métricas', icon: BarChart3 }] : []),
        { key: 'goals', label: 'Metas & Bonos', icon: Target },
      ],
    },
    ...(isSuperAdmin ? [{
      label: 'Administración',
      items: [
        { key: 'auditlog', label: 'God View', icon: Eye },
        { key: 'settings', label: 'Configuración', icon: SettingsIcon },
      ],
    }] : []),
  ];

  const navItems = navGroups.flatMap(g => g.items);

  // A gestor is locked to her own branch/name -- this only drives which
  // cards/rows the UI shows; the real boundary is the RLS policies, which
  // never return other branches' rows in the first place.
  useEffect(() => {
    if (isGestor && profile?.branchName) {
      setSelectedBranch(profile.branchName);
      setSelectedManager(profile.displayName || 'all');
    }
  }, [isGestor, profile]);

  // Memoized: loans/payments can be in the hundreds per branch, and
  // without this every one of these recomputed from scratch on every
  // render of this (large) component -- typing in any search box,
  // opening any modal, toggling the sidebar, all of it.
  const loansWithStatus = useMemo(() => loans.map(calculateLoanStatus), [loans]);

  const filteredLoans = useMemo(() => loansWithStatus.filter(loan => {
    const branchMatch = selectedBranch === 'all' || loan.branch === selectedBranch;
    const managerMatch = selectedManager === 'all' || loan.manager === selectedManager;
    return branchMatch && managerMatch;
  }), [loansWithStatus, selectedBranch, selectedManager]);

  const dashboardStats = useMemo(() => ({
    totalLoaned: filteredLoans.reduce((sum, loan) => sum + loan.loanAmount, 0),
    totalCollected: filteredLoans.reduce((sum, loan) => sum + loan.totalPaid, 0),
    pendingBalance: filteredLoans.reduce((sum, loan) => sum + (loan.totalExpected - loan.totalPaid), 0),
    todaysDueTotal: filteredLoans.reduce((sum, loan) => sum + loan.todaysDue, 0),
    overdueAmount: filteredLoans.filter(loan => loan.daysBehind > 0).reduce((sum, loan) => sum + loan.overdueAmount, 0),
    activeLoans: filteredLoans.filter(loan => loan.status !== 'Pagado').length,
    overdueLoans: filteredLoans.filter(loan => loan.daysBehind > 0).length,
    criticalLoans: filteredLoans.filter(loan => loan.status === 'Crítico').length
  }), [filteredLoans]);

  const filteredPayments = useMemo(() => payments.filter(payment => {
    const branchMatch = selectedBranch === 'all' || payment.branch === selectedBranch;
    const managerMatch = selectedManager === 'all' || payment.manager === selectedManager;
    return branchMatch && managerMatch;
  }), [payments, selectedBranch, selectedManager]);

  if (loansLoading || paymentsLoading || profileLoading || rosterLoading) {
    return (
      <div className="min-h-screen bg-gray-800 dark:bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin-glow rounded-full h-12 w-12 border-4 border-teal-300 border-t-transparent mx-auto mb-4"></div>
          <p className="text-teal-300">Cargando datos...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin && !isGestor) {
    return (
      <div className="min-h-screen bg-gray-800 dark:bg-black flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <p className="text-white font-semibold mb-2">Cuenta sin configurar</p>
          <p className="text-gray-400 text-sm mb-6">
            Tu cuenta no tiene un rol asignado todavía. Contacta al administrador para que configure tu acceso.
          </p>
          <button
            onClick={logout}
            className="text-teal-300 hover:text-teal-200 text-sm transition-colors"
          >
            Cerrar Sesión
          </button>
        </div>
      </div>
    );
  }

  const branches = rosterBranches;
  const branchColorMap = {};
  [...branches].sort().forEach((b, i) => { branchColorMap[b] = BRANCH_LABEL_COLORS[i % BRANCH_LABEL_COLORS.length]; });
  const getBranchColor = (branchName) => branchColorMap[branchName] || BRANCH_LABEL_COLORS[0];
  const gestores = rosterGestores;
  const managers = rosterSupervisors; // Supervisor(es)
  const gestoresYSupervisor = [...gestores, ...managers];

  // Per-gestor branch + the specific supervisor for that branch -- shared
  // by Control de Efectivo and Metas & Bonos, which otherwise have no way
  // to say which branch/supervisor a card belongs to. Deliberately resolves
  // to exactly one supervisor (by matching branch_id) or none at all --
  // never a joined list of every org supervisor, since that reads as
  // "these people share oversight of this branch" which usually isn't what
  // multiple supervisors would mean. Scales to 0, 1, or many supervisors
  // without special-casing; a branch with no supervisor assigned in
  // Configuración just shows no Supervisor pill instead of guessing.
  // A supervisor with no branch_id (Configuración lets this be left blank)
  // means "supervises every branch," not "unassigned" -- so it's the
  // fallback for any gestor whose branch has no specific supervisor, but
  // only when exactly one such global supervisor exists. Two+ global
  // supervisors is genuinely ambiguous (who actually covers this branch?),
  // so that resolves to no supervisor shown rather than guessing.
  const globalSupervisors = rosterStaff.filter(s => s.role === 'supervisor' && !s.branch_id);
  const managerInfo = {};
  rosterStaff.filter(p => p.role === 'gestor').forEach(p => {
    const branchName = rosterRawBranches.find(b => b.id === p.branch_id)?.nombre || null;
    const specificSupervisor = p.branch_id
      ? rosterStaff.find(s => s.role === 'supervisor' && s.branch_id === p.branch_id)
      : null;
    managerInfo[p.display_name] = {
      branch: branchName,
      branchColor: branchName ? getBranchColor(branchName) : null,
      supervisor: specificSupervisor?.display_name
        || (globalSupervisors.length === 1 ? globalSupervisors[0].display_name : null),
    };
  });
  // A gestor only ever has one branch/name to pick from -- used wherever
  // the UI would otherwise offer every branch/gestor as an option.
  const visibleBranches = isGestor && profile?.branchName ? [profile.branchName] : branches;
  const visibleManagers = isGestor && profile?.displayName ? [profile.displayName] : gestoresYSupervisor;

  // Branch and gestor are 1:1, so picking a branch in the header search
  // can just derive the gestor instead of needing its own selector.
  const getGestorForBranch = (branchName) => {
    const branch = rosterRawBranches.find(b => b.nombre === branchName);
    if (!branch) return null;
    const gestor = rosterStaff.find(p => p.role === 'gestor' && p.branch_id === branch.id);
    return gestor?.display_name || null;
  };

  const handleSelectBranch = (branchName) => {
    setSelectedBranch(branchName);
    setSelectedManager(getGestorForBranch(branchName) || 'all');
    setBranchQuery('');
  };

  const handleClearBranch = () => {
    setSelectedBranch('all');
    setSelectedManager('all');
    setBranchQuery('');
  };

  const branchSearchResults = branchQuery.trim()
    ? branches.filter(b => b.toLowerCase().includes(branchQuery.toLowerCase()))
    : branches;

  const handlePayment = async (newPayment, selectedLoan) => {
    try {
      await addPayment(newPayment, user.email);
      await updateLoan(selectedLoan.id, {
        total_paid: selectedLoan.totalPaid + newPayment.amount
      });
      alert(`✅ Pago registrado exitosamente!\n💰 Monto: $${newPayment.amount}\n👤 Cliente: ${selectedLoan.clientName}`);
    } catch (error) {
      console.error('Error registering payment:', error);
      alert('Error al registrar el pago. Intente nuevamente.');
    }
  };

  const handleNewLoan = async (newLoanData) => {
    try {
      const totalExpected = parseFloat(newLoanData.loanAmount) * (1 + parseFloat(newLoanData.interestRate));
      const dailyPayment = totalExpected / parseInt(newLoanData.duration);
      
      const loanToSave = {
        loan_number: `LOAN-${Date.now()}`,
        client_name: newLoanData.clientName,
        customer_id: newLoanData.customerId,
        branch: newLoanData.branch,
        manager: newLoanData.manager,
        start_date: new Date().toISOString().split('T')[0],
        due_date: new Date(Date.now() + parseInt(newLoanData.duration) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        amount: parseFloat(newLoanData.loanAmount),
        interest_rate: parseFloat(newLoanData.interestRate),
        term_months: Math.ceil(parseInt(newLoanData.duration) / 30),
        status: 'active',
        total_paid: 0,
        total_expected: totalExpected,
        daily_payment: dailyPayment,
        duration: parseInt(newLoanData.duration)
      };
      
      if (!user || !user.email) {
        throw new Error('No hay usuario autenticado');
      }

      await addLoan(loanToSave, user.email);

      alert('✅ Préstamo creado exitosamente!');
      setShowNewLoanForm(false);

    } catch (error) {
      console.error('Error in handleNewLoan:', error);

      let userMessage = 'Error al crear el préstamo. ';
      
      if (error.message.includes('organization')) {
        userMessage += 'Problema con la organización. ';
      } else if (error.message.includes('permission')) {
        userMessage += 'Problema de permisos. Verifica las políticas RLS. ';
      } else if (error.message.includes('violates')) {
        userMessage += 'Problema de validación de datos. ';
      } else {
        userMessage += error.message;
      }
      
      userMessage += '\n\n🔍 Revisa la consola para más detalles.';
      
      alert(userMessage);
    }
  };

  const handleQuickPay = (loanId) => {
    setShowPaymentForm(true);
  };

  const handleGenerateReport = async () => {
    try {
      setReportLoading(true);

      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/daily-report`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
            'Content-Type': 'application/json',
          },
        }
      );

      const result = await response.json();

      if (response.ok) {
        alert('✅ ¡Reporte enviado exitosamente a ligia.santana@labanquita.com, guillermo.santana@labanquita.com y ligia.urias@gmail.com!');
      } else {
        throw new Error(result.error || 'Error al generar reporte');
      }
    } catch (error) {
      console.error('Error generating report:', error);
      alert('❌ Error al generar reporte. Revisa la consola para más detalles.');
    } finally {
      setReportLoading(false);
    }
  };

  // Compact single-line-per-loan card, used in every branch drill-down
  // list (Tablero, Prestamos, Cobros del Dia) where there can be dozens/
  // hundreds of loans -
  // same data, far less vertical space per loan.
  const LoanRow = ({ loan, onPayClick, onClick }) => (
    <div
      onClick={onClick ? () => onClick(loan) : undefined}
      className={`card-neon p-3 rounded-lg border-l-4 border-teal-300 ${onClick ? 'cursor-pointer hover:border-teal-400/70 transition-colors' : ''}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <h3 className="font-bold text-white truncate">{loan.clientName}</h3>
          <p className="text-xs text-gray-500">{loan.loan_number || loan.id} • {loan.branch}</p>
          <p className="text-xs text-gray-500">
            Gestor: {loan.manager}
            {managerInfo[loan.manager]?.supervisor && ` • Supervisor: ${managerInfo[loan.manager].supervisor}`}
          </p>
        </div>
        <span className={`flex-shrink-0 px-2 py-1 text-xs font-semibold rounded-full ${
          loan.status === 'Crítico' ? 'bg-red-500/20 text-red-400 glow-mint-sm' :
          loan.status === 'Atrasado' ? 'bg-orange-500/20 text-orange-400' :
          loan.status === 'Pagado' ? 'bg-teal-500/20 text-teal-300' :
          loan.status === 'Pendiente' ? 'bg-yellow-500/20 text-yellow-400' :
          'bg-cyan-500/20 text-cyan-400'
        }`}>
          {loan.status}
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-xs mb-2">
        <div>
          <span className="text-gray-400">Prestado: </span>
          <span className="text-white font-semibold">${loan.loanAmount}</span>
        </div>
        <div>
          <span className="text-gray-400">Pendiente: </span>
          <span className="text-red-400 font-semibold">${(loan.totalExpected - loan.totalPaid).toFixed(2)}</span>
        </div>
        <div>
          <span className="text-gray-400">Cuota: </span>
          <span className="text-cyan-400 font-semibold">${loan.dailyPayment.toFixed(2)}</span>
        </div>
        <div>
          <span className="text-gray-400">Debe HOY: </span>
          <span className={`font-semibold ${loan.todaysDue > loan.dailyPayment ? 'text-red-400' : 'text-teal-300'}`}>
            ${loan.todaysDue.toFixed(2)}
          </span>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className="inline-flex items-center px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded-full text-xs font-semibold">
          Prestado el: {formatDate(loan.startDate)}
        </span>
        <span className="inline-flex items-center px-2 py-0.5 bg-cyan-500/20 text-cyan-300 rounded-full text-xs font-semibold">
          Tasa de Interés: {((loan.interest_rate ?? loan.interestRate ?? 0.1) * 100).toFixed(0)}%
        </span>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center px-2 py-0.5 bg-teal-500/20 text-teal-300 rounded-full text-xs font-semibold">
            Días: {loan.daysElapsed}/{loan.duration} • Vence: {formatDate(loan.dueDate)}
          </span>
          {loan.daysBehind > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 bg-red-500/20 text-red-400 rounded-full text-xs font-semibold">
              {loan.daysBehind}d atrasado
            </span>
          )}
        </div>
        {loan.status !== 'Pagado' && onPayClick && (
          <button
            onClick={(e) => { e.stopPropagation(); onPayClick(loan.id); }}
            className="btn-neon text-black font-bold py-1 px-3 rounded text-xs flex-shrink-0"
          >
            💰 Pagar
          </button>
        )}
      </div>
    </div>
  );

  // Even more compact than LoanRow -- name/status + the two numbers that
  // actually matter day-to-day (Pendiente, Debe HOY). Used for the three
  // high-volume branch lists (Tablero, Prestamos, Cobros del Dia); the
  // fuller LoanRow stays as-is for the customer detail modal, where
  // per-loan detail is the point.
  const CompactLoanRow = ({ loan, onPayClick, onClick }) => (
    <div
      onClick={onClick ? () => onClick(loan) : undefined}
      className={`card-neon p-2.5 rounded-lg border-l-4 border-teal-300 ${onClick ? 'cursor-pointer hover:border-teal-400/70 transition-colors' : ''}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-bold text-white truncate">{loan.clientName}</h4>
            <span className={`flex-shrink-0 px-2 py-0.5 text-xs font-semibold rounded-full ${
              loan.status === 'Crítico' ? 'bg-red-500/20 text-red-400' :
              loan.status === 'Atrasado' ? 'bg-orange-500/20 text-orange-400' :
              loan.status === 'Pagado' ? 'bg-teal-500/20 text-teal-300' :
              loan.status === 'Pendiente' ? 'bg-yellow-500/20 text-yellow-400' :
              'bg-cyan-500/20 text-cyan-400'
            }`}>
              {loan.status}
            </span>
          </div>
          <p className="text-xs text-gray-500 truncate">{loan.branch} • Gestor: {loan.manager}</p>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0 text-sm">
          <span>
            <span className="text-gray-400">Pendiente: </span>
            <span className="text-red-400 font-semibold">${(loan.totalExpected - loan.totalPaid).toFixed(2)}</span>
          </span>
          <span>
            <span className="text-gray-400">Debe HOY: </span>
            <span className={`font-semibold ${loan.todaysDue > loan.dailyPayment ? 'text-red-400' : 'text-teal-300'}`}>
              ${loan.todaysDue.toFixed(2)}
            </span>
          </span>
          {loan.daysBehind > 0 && (
            <span className="text-red-400 font-semibold text-xs">{loan.daysBehind}d atraso</span>
          )}
          {loan.status !== 'Pagado' && onPayClick && (
            <button
              onClick={(e) => { e.stopPropagation(); onPayClick(loan.id); }}
              className="btn-neon text-black font-bold py-1 px-3 rounded text-xs flex-shrink-0"
            >
              💰 Pagar
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const LOANS_PER_PAGE = 25;

  // Shared by the three high-volume branch lists -- renders CompactLoanRow
  // per loan plus Anterior/Siguiente controls once there's more than one
  // page. Page resets implicitly: this only stays mounted while a single
  // branch drill-down is open, so switching branches (which unmounts it)
  // already resets to page 1; within the same branch, Math.min clamps the
  // displayed page if a search narrows the results below the current page.
  const PaginatedLoanList = ({ loans, onPayClick, onClick, emptyMessage }) => {
    const [page, setPage] = useState(1);

    if (loans.length === 0) {
      return (
        <div className="text-center py-12">
          <p className="text-gray-400">{emptyMessage}</p>
        </div>
      );
    }

    const totalPages = Math.max(1, Math.ceil(loans.length / LOANS_PER_PAGE));
    const currentPage = Math.min(page, totalPages);
    const pageLoans = loans.slice((currentPage - 1) * LOANS_PER_PAGE, currentPage * LOANS_PER_PAGE);

    return (
      <>
        <div className="space-y-2">
          {pageLoans.map(loan => (
            <CompactLoanRow key={loan.id} loan={loan} onPayClick={onPayClick} onClick={onClick} />
          ))}
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 text-sm">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 bg-gray-800 border border-teal-500/30 text-teal-300 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ← Anterior
            </button>
            <span className="text-gray-400">
              Página {currentPage} de {totalPages} ({loans.length} préstamos)
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 bg-gray-800 border border-teal-500/30 text-teal-300 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Siguiente →
            </button>
          </div>
        )}
      </>
    );
  };

  const PAST_LOANS_PER_PAGE = 4;

  // Past-loan history inside the customer card: a repeat customer can rack
  // up many finished loans, so this shows 4 at a time (most recent first)
  // with Anterior/Siguiente controls, plus a month filter for when a gestor
  // remembers roughly "about a month ago" but not the exact loan.
  const PaginatedCustomerLoans = ({ loans, emptyMessage }) => {
    const [page, setPage] = useState(1);
    const [monthFilter, setMonthFilter] = useState('');

    if (loans.length === 0) {
      return <p className="text-sm text-gray-500">{emptyMessage}</p>;
    }

    const sorted = [...loans].sort((a, b) => b.startDate - a.startDate);
    const filtered = monthFilter
      ? sorted.filter(l => l.startDate.toISOString().slice(0, 7) === monthFilter)
      : sorted;

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAST_LOANS_PER_PAGE));
    const currentPage = Math.min(page, totalPages);
    const pageLoans = filtered.slice((currentPage - 1) * PAST_LOANS_PER_PAGE, currentPage * PAST_LOANS_PER_PAGE);

    return (
      <>
        <div className="flex items-center gap-2 mb-2">
          <DatePicker
            mode="month"
            value={monthFilter}
            onChange={(v) => { setMonthFilter(v); setPage(1); }}
            placeholder="Filtrar por mes"
            className="bg-gray-800 border border-teal-500/30 text-gray-300 text-xs rounded-lg px-2 py-1"
          />
        </div>

        {filtered.length === 0 ? (
          <p className="text-sm text-gray-500">Sin créditos ese mes.</p>
        ) : (
          <div className="space-y-2">
            {pageLoans.map(l => (
              <LoanRow key={l.id} loan={l} />
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-3 text-xs">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 bg-gray-800 border border-teal-500/30 text-teal-300 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ← Anterior
            </button>
            <span className="text-gray-400">
              Página {currentPage} de {totalPages} ({filtered.length} créditos)
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 bg-gray-800 border border-teal-500/30 text-teal-300 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Siguiente →
            </button>
          </div>
        )}
      </>
    );
  };

  // Full profile for whoever a loan belongs to: personal info plus their
  // whole loan history (current + past), not just the one loan clicked.
  // Loans created before customer_id existed have no reliable id to group
  // by, so those fall back to showing just that one loan's own fields --
  // guessing via name match risks showing a different person's info.
  const CustomerDetailModal = ({ loan, onClose }) => {
    const [customer, setCustomer] = useState(null);
    const [loadingCustomer, setLoadingCustomer] = useState(!!loan.customer_id);

    useEffect(() => {
      if (!loan.customer_id) {
        setLoadingCustomer(false);
        return;
      }
      let cancelled = false;
      const fetchCustomer = async () => {
        setLoadingCustomer(true);
        const { data, error } = await supabase
          .from('customers')
          .select('*, branches(nombre)')
          .eq('id', loan.customer_id)
          .single();
        if (!cancelled) {
          if (!error) setCustomer(data);
          setLoadingCustomer(false);
        }
      };
      fetchCustomer();
      return () => { cancelled = true; };
    }, [loan.customer_id]);

    const customerLoans = loan.customer_id
      ? loansWithStatus.filter(l => l.customer_id === loan.customer_id)
      : [loan];
    const currentLoans = customerLoans.filter(l => l.status !== 'Pagado');
    const pastLoans = customerLoans.filter(l => l.status === 'Pagado');
    const totalLoaned = customerLoans.reduce((sum, l) => sum + l.loanAmount, 0);
    const totalPaid = customerLoans.reduce((sum, l) => sum + l.totalPaid, 0);

    return (
      <div className="fixed inset-0 bg-gray-900/80 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-gray-900 border-2 border-teal-500/50 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-6 border-b border-teal-500/20 sticky top-0 bg-gray-900 z-10">
            <h3 className="text-xl font-bold text-teal-300">{customer?.nombre || loan.clientName}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-white">
              <CloseIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {loadingCustomer ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-teal-300 border-t-transparent"></div>
              </div>
            ) : customer ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-400">Teléfono</p>
                  <p className="text-white font-medium">{customer.telefono || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-400">Sucursal</p>
                  <p className="text-white font-medium">{customer.branches?.nombre || loan.branch}</p>
                </div>
                <div>
                  <p className="text-gray-400">DUI</p>
                  <p className="text-white font-medium">{customer.dui || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-400">Número de Ruta</p>
                  <p className="text-white font-medium">{customer.route_order ?? '—'}</p>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-gray-400">Dirección</p>
                  <p className="text-white font-medium">{customer.direccion || '—'}</p>
                </div>
              </div>
            ) : (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 text-sm text-yellow-400">
                Este préstamo no está vinculado a un perfil de cliente (creado antes de esa función). Mostrando solo los datos del préstamo.
                <p className="text-gray-300 mt-2">Sucursal: {loan.branch} • Gestor: {loan.manager}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-800 p-3 rounded-lg border border-teal-500/20">
                <p className="text-xs text-gray-400">Total Prestado Histórico</p>
                <p className="text-lg font-bold text-white">${totalLoaned.toFixed(2)}</p>
              </div>
              <div className="bg-gray-800 p-3 rounded-lg border border-teal-500/20">
                <p className="text-xs text-gray-400">Total Pagado Histórico</p>
                <p className="text-lg font-bold text-teal-300">${totalPaid.toFixed(2)}</p>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-white mb-2">Créditos Actuales ({currentLoans.length})</h4>
              {currentLoans.length > 0 ? (
                <div className="space-y-2">
                  {currentLoans.map(l => (
                    <LoanRow key={l.id} loan={l} onPayClick={handleQuickPay} />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">Sin créditos activos.</p>
              )}
            </div>

            <div>
              <h4 className="text-sm font-semibold text-white mb-2">Créditos Pasados ({pastLoans.length})</h4>
              <PaginatedCustomerLoans loans={pastLoans} emptyMessage="Sin créditos pasados." />
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Card for a single row of the Historial de Pagos table -- shows the
  // payment's own fields plus a link into that customer's full loan
  // history when the loan it belongs to can still be found.
  const PaymentDetailModal = ({ payment, onClose }) => {
    const relatedLoan = loansWithStatus.find(l => l.id === payment.loan_id);

    return (
      <div className="fixed inset-0 bg-gray-900/80 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="card-neon p-6 rounded-lg w-full max-w-md">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-teal-300">{payment.client_name || 'Cliente'}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-white">
              <CloseIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-400 text-sm">Monto Pagado</span>
              <span className="text-2xl font-bold text-teal-300">${Number(payment.amount).toFixed(2)}</span>
            </div>

            <div className="flex flex-wrap gap-2">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${getBranchColor(payment.branch).bg} ${getBranchColor(payment.branch).text}`}>
                {payment.branch}
              </span>
              <span className="inline-flex items-center px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded-full text-xs font-semibold">
                {formatDate(payment.payment_date)}
              </span>
              {payment.manager && (
                <span className="inline-flex items-center px-2 py-0.5 bg-cyan-500/20 text-cyan-300 rounded-full text-xs font-semibold">
                  Gestor: {payment.manager}
                </span>
              )}
              {payment.payment_type && (
                <span className="inline-flex items-center px-2 py-0.5 bg-teal-500/20 text-teal-300 rounded-full text-xs font-semibold">
                  {payment.payment_type === 'regular' ? 'Pago Regular' : payment.payment_type}
                </span>
              )}
            </div>

            {payment.notes && (
              <div>
                <p className="text-xs text-gray-500 mb-1">Notas</p>
                <p className="text-sm text-gray-300">{payment.notes}</p>
              </div>
            )}

            {relatedLoan && (
              <div className="bg-teal-500/10 border border-teal-500/30 rounded-lg p-3">
                <p className="text-xs text-gray-400 mb-1">Préstamo Relacionado</p>
                <p className="text-sm text-white mb-2">
                  {relatedLoan.loan_number || relatedLoan.id} • Pendiente: ${(relatedLoan.totalExpected - relatedLoan.totalPaid).toFixed(2)}
                </p>
                <button
                  onClick={() => { setDetailPayment(null); setDetailLoan(relatedLoan); }}
                  className="text-xs text-teal-300 hover:text-teal-200 font-semibold"
                >
                  Ver Historial Completo del Cliente →
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const PaymentForm = ({ onClose, onSubmit }) => {
    const [formData, setFormData] = useState({
      loanId: '',
      amount: '',
      notes: ''
    });
    const [loanQuery, setLoanQuery] = useState('');

    const availableLoans = filteredLoans.filter(loan => loan.status !== 'Pagado');
    const selectedLoanForPayment = availableLoans.find(loan => loan.id === formData.loanId) || null;
    const filteredLoanResults = loanQuery.trim()
      ? availableLoans
          .filter(loan => loan.clientName?.toLowerCase().includes(loanQuery.toLowerCase()))
          .sort((a, b) => b.todaysDue - a.todaysDue)
          .slice(0, 8)
      : [];
    // Notas is optional here only -- everywhere else that requires it
    // (Nuevo Cliente, cash give/receive) stays mandatory.
    const isValid = formData.loanId && formData.amount;

    const handleLoanQueryChange = (value) => {
      setLoanQuery(value);
      setFormData({ ...formData, loanId: '' });
    };

    const handleSelectLoanForPayment = (loan) => {
      setLoanQuery(loan.clientName);
      setFormData({ ...formData, loanId: loan.id });
    };

    const handleSubmit = () => {
      if (!formData.loanId || !formData.amount) {
        alert('Por favor seleccione un préstamo e ingrese el monto');
        return;
      }

      const selectedLoan = availableLoans.find(loan => loan.id === formData.loanId);
      if (!selectedLoan) {
        alert('Préstamo no encontrado');
        return;
      }

      const paymentAmount = parseFloat(formData.amount);
      if (paymentAmount <= 0) {
        alert('El monto debe ser mayor a 0');
        return;
      }

      const newPayment = {
        loan_id: formData.loanId,
        amount: paymentAmount,
        payment_date: new Date().toISOString().split('T')[0],
        payment_type: 'regular',
        notes: formData.notes,
        client_name: selectedLoan.clientName,
        branch: selectedLoan.branch,
        manager: selectedLoan.manager
      };

      onSubmit(newPayment, selectedLoan);
      onClose();
    };

    return (
      // Higher than the other modals (z-50): Pagar can be triggered from
      // inside CustomerDetailModal/PaymentDetailModal while they're still
      // open underneath, and DOM order alone put this behind them at equal
      // z-index -- it needs to always win regardless of mount order.
      <div className="fixed inset-0 bg-gray-900/80 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-[60]">
        <div className="card-neon p-6 rounded-lg w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
          <h2 className="text-xl font-bold mb-4 text-teal-300">💰 Registrar Pago (Efectivo)</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Cliente <span className="text-red-400">*</span>
              </label>

              {selectedLoanForPayment ? (
                <div className="flex items-center justify-between p-2 bg-teal-500/10 border border-teal-500/40 rounded-lg">
                  <div className="flex items-center min-w-0">
                    <Check size={16} className="text-teal-300 mr-2 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-white font-medium truncate">{selectedLoanForPayment.clientName}</p>
                      <p className="text-xs text-gray-400 truncate">
                        {selectedLoanForPayment.branch} • Debe: ${selectedLoanForPayment.todaysDue.toFixed(2)}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleLoanQueryChange('')}
                    className="text-xs text-teal-300 hover:text-teal-200 ml-2 flex-shrink-0"
                  >
                    Cambiar
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input
                    type="text"
                    className="w-full p-2 pl-9 bg-gray-800 dark:bg-black border border-teal-500/30 text-white rounded-lg focus:border-teal-400"
                    value={loanQuery}
                    onChange={(e) => handleLoanQueryChange(e.target.value)}
                    placeholder="Buscar por nombre de cliente..."
                  />

                  {loanQuery && (
                    <div className="absolute z-10 mt-1 w-full bg-gray-900 border border-teal-500/30 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                      {filteredLoanResults.length > 0 ? (
                        filteredLoanResults.map(loan => (
                          <button
                            type="button"
                            key={loan.id}
                            onClick={() => handleSelectLoanForPayment(loan)}
                            className="w-full text-left px-3 py-2 hover:bg-teal-500/10 border-b border-gray-800 last:border-b-0"
                          >
                            <p className="text-white text-sm">{loan.clientName}</p>
                            <p className="text-xs text-gray-400">{loan.branch} • Debe: ${loan.todaysDue.toFixed(2)}</p>
                          </button>
                        ))
                      ) : (
                        <p className="px-3 py-3 text-center text-sm text-gray-400">No se encontró ningún préstamo activo</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {selectedLoanForPayment && (
              <div className="bg-teal-500/10 p-3 rounded-lg border border-teal-500/30">
                <h4 className="font-medium text-sm mb-2 text-teal-300">📋 Información del Préstamo:</h4>
                <div className="text-sm space-y-1 text-gray-300">
                  <p><strong>Cliente:</strong> {selectedLoanForPayment.clientName}</p>
                  <p><strong>Sucursal:</strong> {selectedLoanForPayment.branch}</p>
                  <p><strong>Cuota Diaria:</strong> ${selectedLoanForPayment.dailyPayment.toFixed(2)}</p>
                  <p className={`font-bold ${selectedLoanForPayment.daysBehind > 0 ? 'text-red-400' : 'text-teal-300'}`}>
                    <strong>Debe HOY:</strong> ${selectedLoanForPayment.todaysDue.toFixed(2)}
                  </p>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Monto del Pago ($)</label>
              <input
                type="number"
                step="0.01"
                className="w-full p-2 bg-gray-800 dark:bg-black border border-teal-500/30 text-white rounded-lg focus:border-teal-400"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                placeholder="0.00"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Notas (opcional)</label>
              <textarea
                className="w-full p-2 bg-gray-800 dark:bg-black border border-teal-500/30 text-white rounded-lg focus:border-teal-400"
                rows="2"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Observaciones del pago..."
              />
            </div>

            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-sm text-yellow-300">
              ⚠️ Por favor verifica con el cliente que el monto sea el correcto.
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2 px-4 bg-gray-800 border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-700"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!isValid}
                title={!isValid ? 'Selecciona un préstamo e ingresa el monto para continuar' : ''}
                className="flex-1 btn-neon text-black font-bold py-2 px-4 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
              >
                💰 Registrar Pago
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const NewLoanForm = ({ onClose, onSubmit, onGoToCustomers }) => {
    const [formData, setFormData] = useState({
      clientName: '',
      customerId: '',
      branch: visibleBranches[0],
      manager: visibleManagers[0],
      loanAmount: '',
      duration: '',
      interestRate: ''
    });

    const [customers, setCustomers] = useState([]);
    const [loadingCustomers, setLoadingCustomers] = useState(true);
    const [customerSearch, setCustomerSearch] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    useEffect(() => {
      const fetchCustomers = async () => {
        const { data, error } = await supabase
          .from('customers')
          .select('*, branches(nombre, manager)')
          .order('nombre');
        if (!error) setCustomers(data || []);
        setLoadingCustomers(false);
      };
      fetchCustomers();
    }, []);

    const filteredCustomers = customerSearch
      ? customers.filter(c =>
          c.nombre?.toLowerCase().includes(customerSearch.toLowerCase()) ||
          c.telefono?.includes(customerSearch) ||
          c.route_order?.toString().includes(customerSearch)
        ).slice(0, 8)
      : [];

    const handleSearchChange = (value) => {
      setCustomerSearch(value);
      setSelectedCustomer(null);
      setFormData({
        ...formData,
        clientName: '',
        customerId: '',
        branch: visibleBranches[0],
        manager: visibleManagers[0]
      });
    };

    const handleSelectCustomer = (customer) => {
      const customerBranch = customer.branches?.nombre;
      const branchManager = customer.branches?.manager;
      setSelectedCustomer(customer);
      setCustomerSearch(customer.nombre);
      setFormData({
        ...formData,
        clientName: customer.nombre,
        customerId: customer.id,
        branch: branches.includes(customerBranch) ? customerBranch : formData.branch,
        manager: gestoresYSupervisor.includes(branchManager) ? branchManager : formData.manager
      });
    };

    const isValid = selectedCustomer && formData.loanAmount && formData.duration && formData.interestRate;

    const handleSubmit = () => {
      if (!selectedCustomer) {
        alert('Selecciona un cliente existente de la lista. Si no aparece, agrégalo primero en la sección Clientes.');
        return;
      }
      if (!formData.loanAmount || !formData.duration || !formData.interestRate) {
        alert('Por favor complete todos los campos');
        return;
      }
      onSubmit(formData);
    };

    return (
      <div className="fixed inset-0 bg-gray-900/80 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="card-neon p-6 rounded-lg w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
          <h2 className="text-xl font-bold mb-4 text-teal-300">Nuevo Préstamo</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Cliente <span className="text-red-400">*</span>
              </label>

              {selectedCustomer ? (
                <div className="flex items-center justify-between p-2 bg-teal-500/10 border border-teal-500/40 rounded-lg">
                  <div className="flex items-center min-w-0">
                    <Check size={16} className="text-teal-300 mr-2 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-white font-medium truncate">{selectedCustomer.nombre}</p>
                      <p className="text-xs text-gray-400 truncate">
                        {selectedCustomer.branches?.nombre}{selectedCustomer.telefono ? ` • ${selectedCustomer.telefono}` : ''}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleSearchChange('')}
                    className="text-xs text-teal-300 hover:text-teal-200 ml-2 flex-shrink-0"
                  >
                    Cambiar
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input
                    type="text"
                    className="w-full p-2 pl-9 bg-gray-800 dark:bg-black border border-teal-500/30 text-white rounded-lg focus:border-teal-400"
                    value={customerSearch}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    placeholder={loadingCustomers ? 'Cargando clientes...' : 'Buscar por nombre, teléfono o ruta...'}
                    disabled={loadingCustomers}
                  />

                  {customerSearch && (
                    <div className="absolute z-10 mt-1 w-full bg-gray-900 border border-teal-500/30 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                      {filteredCustomers.length > 0 ? (
                        filteredCustomers.map(customer => (
                          <button
                            type="button"
                            key={customer.id}
                            onClick={() => handleSelectCustomer(customer)}
                            className="w-full text-left px-3 py-2 hover:bg-teal-500/10 border-b border-gray-800 last:border-b-0"
                          >
                            <p className="text-white text-sm">{customer.nombre}</p>
                            <p className="text-xs text-gray-400">
                              {customer.branches?.nombre}{customer.telefono ? ` • ${customer.telefono}` : ''}
                            </p>
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-3 text-center">
                          <p className="text-sm text-gray-400 mb-2">No se encontró ningún cliente</p>
                          <button
                            type="button"
                            onClick={onGoToCustomers}
                            className="inline-flex items-center text-xs text-teal-300 hover:text-teal-200 font-semibold"
                          >
                            <UserPlus size={14} className="mr-1" />
                            Agregar Cliente primero
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Sucursal</label>
              <select
                className="w-full p-2 bg-gray-800 dark:bg-black border border-teal-500/30 text-white rounded-lg focus:border-teal-400 disabled:opacity-60 disabled:cursor-not-allowed"
                value={formData.branch}
                onChange={(e) => setFormData({...formData, branch: e.target.value})}
                disabled={!!selectedCustomer}
              >
                {visibleBranches.map(branch => (
                  <option key={branch} value={branch}>{branch}</option>
                ))}
              </select>
              {selectedCustomer && (
                <p className="text-xs text-gray-500 mt-1">Asignada automáticamente según el cliente</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Gestor</label>
              <select
                className="w-full p-2 bg-gray-800 dark:bg-black border border-teal-500/30 text-white rounded-lg focus:border-teal-400 disabled:opacity-60 disabled:cursor-not-allowed"
                value={formData.manager}
                onChange={(e) => setFormData({...formData, manager: e.target.value})}
                disabled={!!selectedCustomer}
              >
                {visibleManagers.map(manager => (
                  <option key={manager} value={manager}>{manager}</option>
                ))}
              </select>
              {selectedCustomer && (
                <p className="text-xs text-gray-500 mt-1">Asignado automáticamente según la sucursal</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Monto del Préstamo ($)</label>
              <input
                type="number"
                step="0.01"
                className="w-full p-2 bg-gray-800 dark:bg-black border border-teal-500/30 text-white rounded-lg focus:border-teal-400"
                value={formData.loanAmount}
                onChange={(e) => setFormData({...formData, loanAmount: e.target.value})}
                placeholder="0.00"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Duración (días)</label>
              <input
                type="number"
                className="w-full p-2 bg-gray-800 dark:bg-black border border-teal-500/30 text-white rounded-lg focus:border-teal-400"
                value={formData.duration}
                onChange={(e) => setFormData({...formData, duration: e.target.value})}
                placeholder="30"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Tasa de Interés (decimal, ej: 0.1 para 10%)</label>
              <input
                type="number"
                step="0.01"
                className="w-full p-2 bg-gray-800 dark:bg-black border border-teal-500/30 text-white rounded-lg focus:border-teal-400"
                value={formData.interestRate}
                onChange={(e) => setFormData({...formData, interestRate: e.target.value})}
                placeholder="0.10"
              />
            </div>
            
            {formData.loanAmount && formData.duration && formData.interestRate && (
              <div className="bg-teal-500/10 p-3 rounded border border-teal-500/30">
                <h4 className="font-medium text-sm mb-2 text-teal-300">Vista Previa del Cálculo:</h4>
                <p className="text-sm text-gray-300">Cuota Diaria: ${((parseFloat(formData.loanAmount) * (1 + parseFloat(formData.interestRate))) / parseInt(formData.duration)).toFixed(2)}</p>
                <p className="text-sm text-gray-300">Total a Pagar: ${(parseFloat(formData.loanAmount) * (1 + parseFloat(formData.interestRate))).toFixed(2)}</p>
              </div>
            )}

            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-sm text-yellow-300">
              ⚠️ Por favor verifica con el cliente que el monto sea el correcto.
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2 px-4 bg-gray-800 border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-700"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!isValid}
                title={!selectedCustomer ? 'Selecciona un cliente existente para continuar' : ''}
                className="flex-1 btn-neon text-black font-bold py-2 px-4 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              >
                Crear Préstamo
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-800 dark:bg-black">
      {/* Header */}
      <div className="header-neon">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-between items-center gap-3 py-4">
            <div className="flex items-center w-full justify-center sm:w-auto sm:justify-start">
              <DollarSign className="h-8 w-8 text-teal-300 mr-3" />
              <h1 className="text-2xl font-bold text-teal-300">La Banquita</h1>
            </div>
            <div className="flex flex-wrap items-center gap-3 sm:space-x-4">
              {isGestor ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-500/10 border border-teal-500/30 text-teal-300 rounded-full text-sm font-semibold">
                  <Building2 className="h-3.5 w-3.5" />
                  {profile?.branchName || selectedBranch}
                </span>
              ) : (
                selectedBranch !== 'all' ? (
                  <div className="flex items-center gap-2 p-2 bg-teal-500/10 border border-teal-500/40 rounded-lg text-sm">
                    <div className="min-w-0">
                      <p className="text-white font-medium truncate">{selectedBranch}</p>
                      {selectedManager !== 'all' && (
                        <p className="text-xs text-gray-400 truncate">Gestor: {selectedManager}</p>
                      )}
                    </div>
                    <button
                      onClick={handleClearBranch}
                      className="text-xs text-teal-300 hover:text-teal-200 ml-2 flex-shrink-0"
                    >
                      Cambiar
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500" />
                    <input
                      type="text"
                      value={branchQuery}
                      onChange={(e) => setBranchQuery(e.target.value)}
                      placeholder="Buscar sucursal..."
                      className="p-2 pl-8 bg-gray-800 dark:bg-black border border-teal-500/30 text-white rounded-md text-sm focus:border-teal-400 w-48"
                    />
                    {branchQuery && (
                      <div className="absolute z-20 mt-1 w-48 bg-gray-900 border border-teal-500/30 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {branchSearchResults.length > 0 ? (
                          branchSearchResults.map(branch => (
                            <button
                              key={branch}
                              onClick={() => handleSelectBranch(branch)}
                              className="w-full text-left px-3 py-2 text-sm text-white hover:bg-teal-500/10 border-b border-gray-800 last:border-b-0"
                            >
                              {branch}
                            </button>
                          ))
                        ) : (
                          <p className="px-3 py-2 text-sm text-gray-400">No se encontró</p>
                        )}
                      </div>
                    )}
                  </div>
                )
              )}

              <div className="flex items-center space-x-3 border-l border-teal-500/30 pl-4 ml-4">
                {!isGestor && (
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-cyan-500 text-black rounded-full flex items-center justify-center text-sm font-semibold glow-mint-sm">
                      {user?.email?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <div className="hidden sm:block">
                      <p className="text-sm font-medium text-white">
                        {user?.email?.split('@')[0] || 'Usuario'}
                      </p>
                      <p className="text-xs text-gray-400">{user?.email}</p>
                    </div>
                  </div>
                )}
                <button
                  onClick={logout}
                  className="flex items-center space-x-1 text-gray-400 hover:text-red-400 transition-colors"
                  title="Cerrar Sesión"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="hidden sm:inline text-sm">Salir</span>
                </button>
              </div>
              
              <div className="flex items-center space-x-2">
                <Award className="h-4 w-4 text-teal-300" />
                <span className="text-sm font-medium text-teal-300">
                  {profile?.role === 'supervisor' ? 'Supervisor' : isAdmin ? 'Admin' : (profile?.displayName || 'Gestor')}
                </span>
              </div>

              {isAdmin && (
                <button
                  onClick={handleGenerateReport}
                  disabled={reportLoading}
                  className="btn-neon text-black font-bold px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-sm"
                >
                  {reportLoading ? (
                    <>
                      <div className="animate-spin-glow rounded-full h-4 w-4 border-2 border-black border-t-transparent mr-2"></div>
                      Generando...
                    </>
                  ) : (
                    <>📧 Generar Reporte</>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="md:flex">
        {/* Desktop sidebar */}
        <aside className={`hidden md:flex md:flex-col border-r border-teal-500/20 sidebar-neon transition-all ${sidebarCollapsed ? 'md:w-16' : 'md:w-60'}`}>
          <button
            onClick={toggleSidebar}
            className="p-3 text-gray-400 hover:text-teal-300 self-end"
            title={sidebarCollapsed ? 'Expandir menú' : 'Contraer menú'}
          >
            {sidebarCollapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
          </button>
          <nav className="flex-1 overflow-y-auto pb-4 px-2">
            {navGroups.map(group => (
              <div key={group.label} className="mb-4">
                {!sidebarCollapsed && (
                  <p className="px-2 mb-1.5 flex items-center gap-2 text-xs uppercase tracking-wide text-gray-400">
                    {group.label}
                    <span className="h-px flex-1 bg-gradient-to-r from-teal-500/30 to-transparent" />
                  </p>
                )}
                {group.items.map(item => {
                  const isActive = activeView === item.key;
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.key}
                      onClick={() => setActiveView(item.key)}
                      title={sidebarCollapsed ? item.label : undefined}
                      className={`nav-item-neon w-full flex items-center gap-3 px-3 py-2 mb-0.5 rounded-lg text-sm ${
                        isActive
                          ? 'nav-item-active text-teal-300 font-semibold'
                          : 'text-gray-300 hover:text-white'
                      }`}
                    >
                      <Icon className="h-4 w-4 flex-shrink-0" />
                      {!sidebarCollapsed && <span>{item.label}</span>}
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>
        </aside>

        <div className="flex-1 min-w-0">
          {/* Mobile nav - hamburger menu instead of horizontal scroll,
              since scrolling tabs wasn't discoverable enough for gestores */}
          <div className="md:hidden relative py-2 px-4 sm:px-6 bg-gray-800 dark:bg-black border-b border-teal-500/20">
            <button
              onClick={() => setMobileMenuOpen(o => !o)}
              className="flex items-center gap-2 text-teal-300 font-semibold py-2 px-4 rounded-lg border border-teal-500/40 bg-teal-500/10"
            >
              {mobileMenuOpen ? <CloseIcon className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              <span>Menú</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${mobileMenuOpen ? 'rotate-180' : ''}`} />
            </button>

            {mobileMenuOpen && (
              <div className="fixed inset-0 z-50 bg-gray-900 dark:bg-black flex flex-col">
                <div className="flex items-center justify-between p-4 border-b border-teal-500/20 flex-shrink-0">
                  <span className="text-teal-300 font-semibold">Menú</span>
                  <button
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-gray-400 hover:text-white"
                  >
                    <CloseIcon className="h-6 w-6" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto py-2 px-3">
                  {navItems.map(item => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.key}
                        onClick={() => { setActiveView(item.key); setMobileMenuOpen(false); }}
                        className={`nav-item-neon w-full flex items-center gap-3 text-left px-4 py-3 mb-1 rounded-lg text-sm ${
                          activeView === item.key ? 'nav-item-active text-teal-300 font-semibold' : 'text-gray-300'
                        }`}
                      >
                        <Icon className="h-4 w-4 flex-shrink-0" />
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                  {isAdmin && (
                    <div className="border-t border-teal-500/20 mt-2 pt-2">
                      <button
                        onClick={() => { handleGenerateReport(); setMobileMenuOpen(false); }}
                        disabled={reportLoading}
                        className="w-full text-left px-4 py-3 text-sm text-teal-300 font-semibold disabled:opacity-50"
                      >
                        {reportLoading ? 'Generando...' : '📧 Generar Reporte'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Suspense fallback={
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin-glow rounded-full h-10 w-10 border-4 border-teal-300 border-t-transparent"></div>
          </div>
        }>
        {activeView === 'dashboard' && (
          <div>
            {!dashboardBranchView ? (
              <>
                {/* Branch summary panels - click into one to see its dashboard */}
                <h2 className="text-2xl font-bold text-teal-300 mb-6">Tablero de Préstamos</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {visibleBranches.map(branchName => {
                    const branchLoans = filteredLoans.filter(l => l.branch === branchName);
                    const overdueCount = branchLoans.filter(l => l.daysBehind > 0).length;
                    const pendingBalance = branchLoans.reduce((sum, l) => sum + (l.totalExpected - l.totalPaid), 0);
                    const dueToday = branchLoans.reduce((sum, l) => sum + l.todaysDue, 0);

                    return (
                      <button
                        key={branchName}
                        onClick={() => setDashboardBranchView(branchName)}
                        className="card-neon p-5 rounded-lg text-left hover:border-teal-400/50 transition-all"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-bold text-lg text-white">{branchName}</h3>
                          <ChevronRight className="h-5 w-5 text-teal-300 flex-shrink-0" />
                        </div>
                        <p className="text-sm text-gray-400 mb-3">
                          {branchLoans.length} préstamo{branchLoans.length !== 1 ? 's' : ''}
                          {overdueCount > 0 && (
                            <span className="text-red-400 font-semibold"> • {overdueCount} atrasado{overdueCount !== 1 ? 's' : ''}</span>
                          )}
                        </p>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-gray-400">Saldo Pendiente:</p>
                            <p className="font-semibold text-red-400">${pendingBalance.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-gray-400">Debe HOY:</p>
                            <p className="font-semibold text-teal-300">${dueToday.toFixed(2)}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </>
            ) : (
              <>
                {/* Drill-down: full dashboard for the selected branch */}
                <button
                  onClick={() => setDashboardBranchView(null)}
                  className="flex items-center text-teal-300 hover:text-teal-200 mb-4 text-sm"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Volver a Sucursales
                </button>

                {(() => {
                  const branchLoans = filteredLoans.filter(l => l.branch === dashboardBranchView);
                  const branchStats = {
                    totalLoaned: branchLoans.reduce((sum, loan) => sum + loan.loanAmount, 0),
                    totalCollected: branchLoans.reduce((sum, loan) => sum + loan.totalPaid, 0),
                    pendingBalance: branchLoans.reduce((sum, loan) => sum + (loan.totalExpected - loan.totalPaid), 0),
                    todaysDueTotal: branchLoans.reduce((sum, loan) => sum + loan.todaysDue, 0)
                  };
                  const urgentLoans = branchLoans
                    .filter(loan => loan.daysBehind > 0)
                    .sort((a, b) => b.daysBehind - a.daysBehind)
                    .filter(loan => {
                      if (!dashboardSearchTerm.trim()) return true;
                      const term = dashboardSearchTerm.toLowerCase();
                      return loan.clientName?.toLowerCase().includes(term) ||
                        (loan.loan_number || loan.id)?.toLowerCase().includes(term);
                    });

                  return (
                    <>
                      <div className="mb-6">
                        <h3 className="text-lg font-semibold text-white mb-4">{dashboardBranchView}</h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                          <StatCard title="Total Prestado" value={branchStats.totalLoaned} />
                          <StatCard title="Total Cobrado" value={branchStats.totalCollected} />
                          <StatCard title="Saldo Pendiente" value={branchStats.pendingBalance} />
                          <StatCard
                            title="Debe Cobrarse HOY"
                            value={branchStats.todaysDueTotal}
                            subtitle="total acumulado diario"
                          />
                        </div>
                      </div>

                      <div className="mb-8">
                        <h3 className="text-lg font-semibold text-white mb-4">
                          Préstamos que Requieren Atención Inmediata
                          <span className="ml-2 text-sm text-gray-500">
                            ({urgentLoans.length} préstamos)
                          </span>
                        </h3>

                        <div className="relative mb-4">
                          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                          <input
                            type="text"
                            value={dashboardSearchTerm}
                            onChange={(e) => setDashboardSearchTerm(e.target.value)}
                            placeholder="Buscar por cliente o número de préstamo..."
                            className="w-full p-2 pl-9 bg-gray-800 dark:bg-black border border-teal-500/30 text-white rounded-lg focus:border-teal-400"
                          />
                        </div>

                        <PaginatedLoanList
                          loans={urgentLoans}
                          onPayClick={handleQuickPay}
                          onClick={setDetailLoan}
                          emptyMessage={dashboardSearchTerm ? 'No se encontraron préstamos' : '🎉 ¡Excelente! No hay préstamos atrasados en este momento.'}
                        />
                      </div>
                    </>
                  );
                })()}
              </>
            )}
          </div>
        )}

        {activeView === 'loans' && (
          <div>
            <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
              <h2 className="text-2xl font-bold text-teal-300">Gestión de Préstamos</h2>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setShowPaymentForm(true)}
                  className="btn-neon text-black font-bold px-4 py-2 rounded-lg flex items-center"
                >
                  💰 Registrar Pago
                </button>
                <button
                  onClick={() => setShowNewLoanForm(true)}
                  className="btn-neon text-black font-bold px-4 py-2 rounded-lg flex items-center"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nuevo Préstamo
                </button>
              </div>
            </div>

            {!loansBranchView ? (
              <>
                {/* Branch summary panels - click into one to see its loans */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {visibleBranches.map(branchName => {
                    const branchLoans = filteredLoans.filter(l => l.branch === branchName);
                    const overdueCount = branchLoans.filter(l => l.daysBehind > 0).length;
                    const pendingBalance = branchLoans.reduce((sum, l) => sum + (l.totalExpected - l.totalPaid), 0);
                    const dueToday = branchLoans.reduce((sum, l) => sum + l.todaysDue, 0);

                    return (
                      <button
                        key={branchName}
                        onClick={() => { setLoansBranchView(branchName); setLoanSearchTerm(''); }}
                        className="card-neon p-5 rounded-lg text-left hover:border-teal-400/50 transition-all"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-bold text-lg text-white">{branchName}</h3>
                          <ChevronRight className="h-5 w-5 text-teal-300 flex-shrink-0" />
                        </div>
                        <p className="text-sm text-gray-400 mb-3">
                          {branchLoans.length} préstamo{branchLoans.length !== 1 ? 's' : ''}
                          {overdueCount > 0 && (
                            <span className="text-red-400 font-semibold"> • {overdueCount} atrasado{overdueCount !== 1 ? 's' : ''}</span>
                          )}
                        </p>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-gray-400">Saldo Pendiente:</p>
                            <p className="font-semibold text-red-400">${pendingBalance.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-gray-400">Debe HOY:</p>
                            <p className="font-semibold text-teal-300">${dueToday.toFixed(2)}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {filteredLoans.length === 0 && (
                  <div className="text-center py-12">
                    <DollarSign className="mx-auto h-12 w-12 text-teal-300 mb-4" />
                    <h3 className="text-lg font-medium text-white mb-2">No hay préstamos</h3>
                    <p className="text-gray-400">Comience creando un nuevo préstamo.</p>
                    <button
                      onClick={() => setShowNewLoanForm(true)}
                      className="mt-4 btn-neon text-black font-bold px-4 py-2 rounded-lg"
                    >
                      Crear Primer Préstamo
                    </button>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Drill-down: all loans for the selected branch, with search */}
                <button
                  onClick={() => { setLoansBranchView(null); setLoanSearchTerm(''); }}
                  className="flex items-center text-teal-300 hover:text-teal-200 mb-4 text-sm"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Volver a Sucursales
                </button>

                <h3 className="text-lg font-semibold text-white mb-3">{loansBranchView}</h3>

                <div className="relative mb-4">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input
                    type="text"
                    value={loanSearchTerm}
                    onChange={(e) => setLoanSearchTerm(e.target.value)}
                    placeholder="Buscar por cliente o número de préstamo..."
                    className="w-full p-2 pl-9 bg-gray-800 dark:bg-black border border-teal-500/30 text-white rounded-lg focus:border-teal-400"
                  />
                </div>

                {(() => {
                  const branchLoans = filteredLoans
                    .filter(l => l.branch === loansBranchView)
                    .filter(l => {
                      if (!loanSearchTerm.trim()) return true;
                      const term = loanSearchTerm.toLowerCase();
                      return l.clientName?.toLowerCase().includes(term) ||
                        (l.loan_number || l.id)?.toLowerCase().includes(term);
                    });

                  return (
                    <PaginatedLoanList
                      loans={branchLoans}
                      onPayClick={handleQuickPay}
                      onClick={setDetailLoan}
                      emptyMessage={loanSearchTerm ? 'No se encontraron préstamos' : 'No hay préstamos en esta sucursal'}
                    />
                  );
                })()}
              </>
            )}
          </div>
        )}

        {activeView === 'payments' && (
          <div>
            <h2 className="text-2xl font-bold text-teal-300 mb-6">Historial de Pagos</h2>

            <div className="relative mb-4">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                value={paymentSearchTerm}
                onChange={(e) => setPaymentSearchTerm(e.target.value)}
                placeholder="Buscar por cliente..."
                className="w-full p-2 pl-9 bg-gray-800 dark:bg-black border border-teal-500/30 text-white rounded-lg focus:border-teal-400"
              />
            </div>

            {(() => {
              const visiblePayments = filteredPayments.filter(payment => {
                if (!paymentSearchTerm.trim()) return true;
                return payment.client_name?.toLowerCase().includes(paymentSearchTerm.toLowerCase());
              });

              return (
                <div className="card-neon rounded-lg overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-700/50 dark:bg-black/50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Cliente</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Sucursal</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Fecha</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Monto</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-teal-500/20">
                      {visiblePayments.length > 0 ? (
                        visiblePayments.map((payment) => (
                          <tr
                            key={payment.id}
                            onClick={() => setDetailPayment(payment)}
                            className="cursor-pointer hover:bg-teal-500/5 transition-colors"
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">{payment.client_name || 'Cliente'}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${getBranchColor(payment.branch).bg} ${getBranchColor(payment.branch).text}`}>
                                {payment.branch}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="inline-flex items-center px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded-full text-xs font-semibold">
                                {formatDate(payment.payment_date)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="inline-flex items-center px-2 py-0.5 bg-emerald-500/15 text-emerald-300 rounded-full text-xs font-semibold">
                                ${payment.amount}
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                            {paymentSearchTerm ? 'No se encontraron pagos' : 'No hay pagos registrados'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
        )}

        {activeView === 'collections' && (
          <div>
            <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
              <h2 className="text-2xl font-bold text-teal-300">Cobros del Día</h2>
              <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                <button
                  onClick={() => setShowPaymentForm(true)}
                  className="btn-neon text-black font-bold px-4 py-2 rounded-lg"
                >
                  💰 Registrar Pago
                </button>
                <div className="text-right">
                  <p className="text-sm text-gray-400">Total a cobrar HOY</p>
                  <p className="text-2xl font-bold text-teal-300">${dashboardStats.todaysDueTotal.toFixed(2)}</p>
                </div>
                <div className="hidden sm:flex items-center space-x-2">
                  <Calendar className="h-5 w-5 text-teal-300" />
                  <span className="text-sm text-gray-400">{formatDate(new Date())}</span>
                </div>
              </div>
            </div>

            {!collectionsBranchView ? (
              <>
                {/* Branch summary panels - click into one to see today's collections */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {visibleBranches.map(branchName => {
                    const branchLoans = filteredLoans.filter(l => l.branch === branchName && l.status !== 'Pagado');
                    const overdueCount = branchLoans.filter(l => l.daysBehind > 0).length;
                    const dueToday = branchLoans.reduce((sum, l) => sum + l.todaysDue, 0);

                    return (
                      <button
                        key={branchName}
                        onClick={() => { setCollectionsBranchView(branchName); setCollectionSearchTerm(''); }}
                        className="card-neon p-5 rounded-lg text-left hover:border-teal-400/50 transition-all"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-bold text-lg text-white">{branchName}</h3>
                          <ChevronRight className="h-5 w-5 text-teal-300 flex-shrink-0" />
                        </div>
                        <p className="text-sm text-gray-400 mb-3">
                          {branchLoans.length} por cobrar
                          {overdueCount > 0 && (
                            <span className="text-red-400 font-semibold"> • {overdueCount} atrasado{overdueCount !== 1 ? 's' : ''}</span>
                          )}
                        </p>
                        <div>
                          <p className="text-gray-400 text-sm">Debe HOY:</p>
                          <p className="font-semibold text-teal-300 text-lg">${dueToday.toFixed(2)}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </>
            ) : (
              <>
                {/* Drill-down: today's collections for the selected branch, with search */}
                <button
                  onClick={() => { setCollectionsBranchView(null); setCollectionSearchTerm(''); }}
                  className="flex items-center text-teal-300 hover:text-teal-200 mb-4 text-sm"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Volver a Sucursales
                </button>

                <h3 className="text-lg font-semibold text-white mb-3">{collectionsBranchView}</h3>

                <div className="relative mb-4">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input
                    type="text"
                    value={collectionSearchTerm}
                    onChange={(e) => setCollectionSearchTerm(e.target.value)}
                    placeholder="Buscar por cliente..."
                    className="w-full p-2 pl-9 bg-gray-800 dark:bg-black border border-teal-500/30 text-white rounded-lg focus:border-teal-400"
                  />
                </div>

                {(() => {
                  const branchLoans = filteredLoans
                    .filter(l => l.branch === collectionsBranchView && l.status !== 'Pagado')
                    .filter(l => !collectionSearchTerm.trim() || l.clientName?.toLowerCase().includes(collectionSearchTerm.toLowerCase()))
                    .sort((a, b) => b.daysBehind - a.daysBehind);

                  return (
                    <PaginatedLoanList
                      loans={branchLoans}
                      onPayClick={handleQuickPay}
                      onClick={setDetailLoan}
                      emptyMessage={collectionSearchTerm ? 'No se encontraron préstamos' : '🎉 No hay cobros pendientes en esta sucursal'}
                    />
                  );
                })()}
              </>
            )}
          </div>
        )}

        {activeView === 'customers' && (
          <Customers profile={profile} branches={rosterRawBranches} />
        )}

        {activeView === 'metrics' && isAdmin && (
          <div>
            <h2 className="text-2xl font-bold text-teal-300 mb-6">Dashboard de Métricas</h2>
            <MetricsDashboard
              loans={loansWithStatus}
              payments={filteredPayments}
              filteredLoans={filteredLoans}
              userEmail={user?.email}
              onSelectCustomer={setDetailLoan}
            />
          </div>
        )}

        {activeView === 'goals' && (isAdmin || isGestor) && (
          <div>
            <h2 className="text-2xl font-bold text-teal-300 mb-6">Metas y Desempeño</h2>
            <GoalsPerformanceTracker
              filteredLoans={filteredLoans}
              filteredPayments={filteredPayments}
              managers={isGestor ? [profile.displayName] : gestores}
              managerInfo={managerInfo}
              readOnly={isGestor}
              userEmail={user?.email}
            />
          </div>
        )}

        {activeView === 'auditlog' && isSuperAdmin && (
          <div>
            <h2 className="text-2xl font-bold text-teal-300 mb-6">🔍 God View — Auditoría</h2>
            <AuditLog />
          </div>
        )}

        {activeView === 'settings' && isSuperAdmin && (
          <div>
            <h2 className="text-2xl font-bold text-teal-300 mb-6">⚙️ Configuración</h2>
            <Settings branches={rosterRawBranches} staff={rosterStaff} loading={rosterLoading} onChange={refreshRoster} createdBy={user?.email} />
          </div>
        )}

        {activeView === 'cashcontrol' && (
          <div>
            <CashControlSystem
              filteredLoans={filteredLoans}
              filteredPayments={filteredPayments}
              managers={isGestor ? [profile.displayName] : gestores}
              managerInfo={managerInfo}
              isGestor={isGestor}
              userEmail={user?.email}
            />
          </div>
        )}
        </Suspense>
      </div>
        </div>
      </div>

      {/* Modals */}
      {showNewLoanForm && (
        <NewLoanForm
          onClose={() => setShowNewLoanForm(false)}
          onSubmit={handleNewLoan}
          onGoToCustomers={() => {
            setShowNewLoanForm(false);
            setActiveView('customers');
          }}
        />
      )}

      {showPaymentForm && (
        <PaymentForm
          onClose={() => setShowPaymentForm(false)}
          onSubmit={handlePayment}
        />
      )}

      {detailLoan && (
        <CustomerDetailModal
          loan={detailLoan}
          onClose={() => setDetailLoan(null)}
        />
      )}

      {detailPayment && (
        <PaymentDetailModal
          payment={detailPayment}
          onClose={() => setDetailPayment(null)}
        />
      )}
    </div>
  );
};

const App = () => {
  return (
    <ThemeProvider>
      <Router>
        <AuthProvider>
          <Routes>
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <LoanTrackerApp />
                </ProtectedRoute>
              }
            />
          </Routes>
          <ZoomControl />
          <UpdateBanner />
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
};

export default App;